/**
 * Authentication helper functions for USL token-based authentication flows
 */

import type { TokenTypes, UslScenario } from '@workivate/tho-web-shared';
import { AuthClient, AppLogger, LogTypes } from '@workivate/tho-web-shared';

import { getStore } from 'app/hermes-redux';
import { logout } from 'state/session/actions';

import { callAuthorizeWithToken, performAuthCheck } from './run-with-usl';
import { setUnexpectedAuthError } from 'globals/context/disaster-recovery-context';

/**
 * Flags the unexpected auth error and logs out.
 *
 * Used whenever an unexpected auth failure occurs and we need to redirect the user to the
 * login page with the TroubleshootingPanel visible.
 *
 * setUnexpectedAuthError(true) internally calls saveAuthErrorIds() before writing the flag,
 * so auth IDs are preserved in sessionStorage before logout() clears them from localStorage.
 *
 * Note: both hard and soft logout clear browser auth storage (OAuth tokens etc.), so
 * neither preserves the session for a page refresh. The difference is only whether the
 * backend session is also terminated via the SSO logout endpoint.
 *
 * @param isHard - Controls whether the logout hits the SSO logout endpoint.
 *   - Use `isHard: true` only when the backend session is still valid AND the user does
 *     not need to see the TroubleshootingPanel. A hard logout redirects to the SSO logout
 *     endpoint, taking the user away from Hermes entirely before the panel can be shown.
 *   - Use `isHard: false` (preferred for unexpected auth errors) when the backend session
 *     has already ended (e.g. token exchange failure), or when you want the user to land
 *     on the Hermes login page where the TroubleshootingPanel is displayed.
 */
export const handleUnexpectedAuthError = async ({
  isHard,
}: {
  isHard: boolean;
}): Promise<void> => {
  setUnexpectedAuthError(true);
  await getStore().dispatch(logout({ isHard }));
};

/**
 * Handles authentication with token and performs auth check.
 * Logs out and shows the TroubleshootingPanel on failure.
 *
 * This function is used by multiple authentication flows:
 * - X_USL_OTT: SSOX with x-usl-ott parameter
 * - USERTOKEN_AND_OTT: SSOX with userToken and OTT
 * - USERTOKEN: Deeplink flow
 * - TOKEN: IDP initiated SSO
 *
 * @param token - The authentication token
 * @param tokenType - The type of token being used
 */
export const handleTokenAuthentication = async ({
  token,
  tokenType,
}: {
  token: string;
  tokenType: TokenTypes;
}): Promise<void> => {
  try {
    await callAuthorizeWithToken({ token, tokenType });
    await performAuthCheck();
  } catch (error) {
    AppLogger.logWithOptions({
      message: 'Error in handleTokenAuthentication',
      level: LogTypes.Error,
      error,
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
      context: { token, tokenType },
    });

    // Token exchange failed — no valid backend session exists, so isHard: false.
    await handleUnexpectedAuthError({ isHard: false });
  }
};

export const handleUslSignUp = async ({
  lang,
  token,
  tokenType,
  uslScenario,
}: {
  lang?: string;
  token: string;
  tokenType: TokenTypes;
  uslScenario: UslScenario;
}): Promise<void> => {
  await AuthClient.registerAndAuthorize({
    clientName: 'web',
    isSSOX: false,
    lang,
    token,
    tokenType,
    uslScenario,
  });
};
